import { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useT } from '../i18n/translations';
import { clearAuth, getCurrentUser, getUserDisplayName } from '../authStorage';
import { getRoomByToken, uploadMeetingRecording } from '../meetingRoomApi';
import { MeetingSignaling } from '../meetingSignaling';
import { WebRTCManager } from '../webrtc';
import { MeetingRecorder } from '../meetingRecorder';
import VideoGrid from './VideoGrid';
import MeetingControls from './MeetingControls';
import MeetingLobby from './MeetingLobby';
import MeetingRoomLogin from './MeetingRoomLogin';
import MeetingChat from './MeetingChat';
import LanguageSelector from '../i18n/LanguageSelector';
import { CreateVoteModal, VotePopup, DecisionsList } from './MeetingVote';
import EditMeetingDetailsModal from './EditMeetingDetailsModal';
import './MeetingRoomPage.css';

export default function MeetingRoomPage() {
  const { token } = useParams();
  const navigate = useNavigate();

  // ─── Auth gate ────────────────────────────────────────────────
  // Anyone can open the meeting link; if not signed in yet, show a sign-in
  // form before loading the room (server re-checks the invite on every load).
  const [authed, setAuthed] = useState(!!getCurrentUser());
  const [needsAuth, setNeedsAuth] = useState(!authed); // Assume needs auth if not logged in, until API says otherwise.

  // The meeting link is opened directly (shared link, or a fresh reload) far more
  // often than navigated to from within the app, so there is frequently no
  // browser-history entry to go back to — navigate(-1) then silently does
  // nothing and the room stays on screen looking "stuck". Go to a route that
  // always exists instead.
  const exitRoom = useCallback(() => {
    navigate(authed ? '/app/meetings' : '/', { replace: true });
  }, [navigate, authed]);

  // ─── Room meta ────────────────────────────────────────────────
  const [roomInfo, setRoomInfo]   = useState(null);
  const [loadError, setLoadError] = useState('');
  
  // ─── Guest flow ───────────────────────────────────────────────
  const [guestName, setGuestName] = useState('');
  const [guestPhotoData, setGuestPhotoData] = useState(null);
  const [guestWaitingState, setGuestWaitingState] = useState(''); // '' | 'waiting' | 'denied'
  const [waitingGuestsList, setWaitingGuestsList] = useState([]); // Array of { guestToken, guestName }
  // Durable per-room id, persisted across reloads — lets a guest who was
  // already approved reconnect straight into the call instead of landing
  // back in the waiting room every time the tab refreshes.
  const [guestToken] = useState(() => {
    if (!token) return null;
    const key = `mtg_guest_token_${token}`;
    let stored = sessionStorage.getItem(key);
    if (!stored) {
      stored = crypto.randomUUID ? crypto.randomUUID() : `${Date.now()}-${Math.random().toString(36).slice(2)}`;
      sessionStorage.setItem(key, stored);
    }
    return stored;
  });

  // ─── Lobby ────────────────────────────────────────────────────
  const [inLobby, setInLobby] = useState(true);  // show lobby before joining
  const [signInFromLobby, setSignInFromLobby] = useState(false); // guest chose to sign in instead

  // ─── Media state ──────────────────────────────────────────────
  const [permState, setPermState]       = useState('pending'); // pending | granted | denied
  const [localStream, setLocalStream]   = useState(null);
  const [micOn, setMicOn]               = useState(true);
  const [cameraOn, setCameraOn]         = useState(false);
  const [screenSharing, setScreenSharing] = useState(false);

  // ─── Peers ────────────────────────────────────────────────────
  // Map: connectionId → { connectionId, stream, displayName, userId, micOn, cameraOn }
  const [peers, setPeers] = useState(new Map());

  // ─── Call lifecycle ───────────────────────────────────────────
  const [callState, setCallState] = useState('idle'); // idle | joining | active | ended
  const [statusMsg, setStatusMsg] = useState('');

  // ─── Chat ─────────────────────────────────────────────────────
  const [chatOpen,         setChatOpen]         = useState(false);
  const [chatMsgs,         setChatMsgs]         = useState([]);
  const [unreadChat,       setUnreadChat]       = useState(0);
  const [participantsOpen, setParticipantsOpen] = useState(false);

  // ─── Voting ───────────────────────────────────────────────────
  // Map: voteId → vote object from server (source of truth, never locally deleted)
  const [votes,          setVotes]          = useState(new Map());
  // Map: voteId → optionIndex the local user chose
  const [myVotes,        setMyVotes]        = useState(new Map());
  // Set: voteIds whose popup the local user has closed (still appear in list)
  const [hiddenPopups,   setHiddenPopups]   = useState(new Set());
  const [createVoteOpen, setCreateVoteOpen] = useState(false);
  const [decisionsOpen,  setDecisionsOpen]  = useState(false);
  const [editDetailsOpen, setEditDetailsOpen] = useState(false);
  const [detailVoteId,   setDetailVoteId]   = useState(null); // voteId being viewed in list detail

  // ─── Recording ────────────────────────────────────────────────
  const [isRecording,   setIsRecording]   = useState(false);
  const [recordingBusy, setRecordingBusy] = useState(false); // saving/uploading after stop
  const [recordingMsg,  setRecordingMsg]  = useState('');

  // Refs for signaling + WebRTC (not re-created on renders)
  const sigRef = useRef(null);
  const rtcRef = useRef(null);
  const screenTrackRef = useRef(null);
  // Kept current so the screenTrack.onended callback always sees fresh values
  const localStreamRef = useRef(null);
  const cameraOnRef    = useRef(true);
  const micOnRef       = useRef(true);
  const recorderRef    = useRef(null);

  const t = useT();
  const me = getCurrentUser();
  const myName = guestName || (authed ? (getUserDisplayName() || me?.email || 'Me') : '');
  const myPhotoData = guestPhotoData || me?.photoData || null;
  const isHost = roomInfo?.hostUserId === me?.id;

  // ─── 1. Load room info ──────────────────────────────────────────
  // If the meeting is open, allow access. Otherwise, require auth.
  useEffect(() => {
    if (!token) return;
    getRoomByToken(token)
      .then(info => {
        setRoomInfo(info);
        if (info.meeting?.isOpen) {
          setNeedsAuth(false);
        } else {
          setNeedsAuth(!authed);
        }
      })
      .catch(e => {
        if (e.status === 401) {
          setNeedsAuth(true);
          if (authed) {
            clearAuth();
            setAuthed(false);
          }
        } else {
          setLoadError(e.message);
        }
      });
  }, [token, authed]);

  // Keep refs in sync with state for use in callbacks that can't close over fresh state
  useEffect(() => { localStreamRef.current = localStream; }, [localStream]);
  useEffect(() => { cameraOnRef.current    = cameraOn;    }, [cameraOn]);
  useEffect(() => { micOnRef.current       = micOn;       }, [micOn]);

  // ─── 2. Lobby → user clicks Join ─────────────────────────────
  function handleLobbyJoin({ stream, micOn: m, cameraOn: c, customName, customPhotoData }) {
    setLocalStream(stream);
    setMicOn(m);
    setCameraOn(c);
    setPermState(stream ? 'granted' : 'denied');
    if (customName) setGuestName(customName);
    if (customPhotoData) setGuestPhotoData(customPhotoData);
    setInLobby(false);
    setCallState('connecting');
    setStatusMsg('Connecting...');
  }

  // ─── 3. Start signaling + WebRTC once user has joined lobby ──
  useEffect(() => {
    // Read the stream from the ref, not the state value: swapping it (screen
    // share) must not re-run this effect, or the whole call is torn down and
    // rejoined — which for a guest means landing back in the waiting room.
    const joinStream = localStreamRef.current;
    if (inLobby || permState !== 'granted' || !joinStream || !token) return;

    setCallState('joining');
    setStatusMsg(t('room.connecting'));

    const sig = new MeetingSignaling();
    sigRef.current = sig;

    sig.on('meetingEnded', () => {
      setCallState('ended');
      setVotes(new Map());
      setHiddenPopups(new Set());
      setDetailVoteId(null);
      cleanup();
    });

    sig.on('waitingForHostApproval', () => {
      setGuestWaitingState('waiting');
      setStatusMsg('');
    });
    sig.on('guestApproved', () => {
      setGuestWaitingState('');
      sigRef.current.retryJoinRoom(guestName, guestPhotoData);
    });
    sig.on('guestDenied', () => {
      setGuestWaitingState('denied');
      sigRef.current.stop();
    });
    sig.on('guestWaiting', (guestInfo) => {
      setWaitingGuestsList(prev =>
        prev.some(g => g.guestToken === guestInfo.guestToken) ? prev : [...prev, guestInfo]
      );
    });

    sig.on('meetingDetailsUpdated', ({ title, goal }) => {
      setRoomInfo(prev => prev ? { ...prev, meeting: { ...prev.meeting, title, goal } } : prev);
    });

    sig.on('peerMediaState', ({ connectionId, micOn: m, cameraOn: c, screenSharing: s }) => {
      setPeers(prev => {
        const next = new Map(prev);
        const peer = next.get(connectionId);
        if (peer) next.set(connectionId, { ...peer, micOn: m, cameraOn: c, screenSharing: !!s });
        return next;
      });
    });

    sig.on('reconnecting', () => setStatusMsg(t('room.reconnecting')));
    sig.on('reconnected',  () => setStatusMsg(''));
    sig.on('disconnected', () => setStatusMsg(t('room.disconnected')));
    sig.on('error', msg => setStatusMsg(msg));

    // ── Chat events ───────────────────────────────────────────
    sig.on('chatMessage', msg => {
      setChatMsgs(prev => [...prev, msg]);
      setChatOpen(prev => {
        if (!prev) setUnreadChat(u => u + 1);
        return prev;
      });
    });

    // ── Vote events ───────────────────────────────────────────
    const upsertVote = vote => {
      setVotes(prev => {
        const next = new Map(prev);
        next.set(vote.voteId, vote);
        return next;
      });
    };
    sig.on('voteCreated', upsertVote);
    sig.on('voteUpdated', upsertVote);
    sig.on('voteDismissed', ({ voteId }) => {
      setVotes(prev => { const n = new Map(prev); n.delete(voteId); return n; });
      setMyVotes(prev => { const n = new Map(prev); n.delete(voteId); return n; });
      setHiddenPopups(prev => { const n = new Set(prev); n.delete(voteId); return n; });
      setDetailVoteId(prev => prev === voteId ? null : prev);
    });

    const rtc = new WebRTCManager(sig, joinStream);
    rtcRef.current = rtc;

    rtc.on('remoteStream', ({ connectionId, stream, userId, displayName, photoData }) => {
      setPeers(prev => {
        const next = new Map(prev);
        const existing = next.get(connectionId) ?? {};
        next.set(connectionId, {
          connectionId,
          stream,
          userId,
          displayName: displayName || existing.displayName || 'Participant',
          photoData: photoData || existing.photoData || null,
          micOn: existing.micOn ?? true,
          cameraOn: existing.cameraOn ?? true,
          screenSharing: existing.screenSharing ?? false,
        });
        return next;
      });
    });

    rtc.on('peerLeft', ({ connectionId }) => {
      setPeers(prev => {
        const next = new Map(prev);
        next.delete(connectionId);
        return next;
      });
    });

    // peerJoined from signaling adds meta before stream arrives
    sig.on('peerJoined', ({ connectionId, userId, displayName, photoData }) => {
      setPeers(prev => {
        const next = new Map(prev);
        if (!next.has(connectionId)) {
          next.set(connectionId, {
            connectionId, stream: null, userId,
            displayName: displayName || 'Participant',
            photoData: photoData || null,
            micOn: true, cameraOn: true, screenSharing: false,
          });
        }
        return next;
      });
    });

    // roomPeers: existing participants when we join
    sig.on('roomPeers', peers => {
      peers.forEach(({ connectionId, userId, displayName, photoData }) => {
        setPeers(prev => {
          const next = new Map(prev);
          if (!next.has(connectionId)) {
            next.set(connectionId, {
              connectionId, stream: null, userId,
              displayName: displayName || 'Participant',
              photoData: photoData || null,
              micOn: true, cameraOn: true,
            });
          }
          return next;
        });
      });
    });

    sig.start(token, guestName, guestPhotoData, guestToken)
      .then(() => {
        setCallState('active');
        setStatusMsg('');
      })
      .catch(e => {
        setCallState('idle');
        setStatusMsg(`Failed to connect: ${e.message}`);
      });

    return () => cleanup();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [inLobby, permState, token]);

  // ─── Cleanup helper ───────────────────────────────────────────
  const cleanup = useCallback(() => {
    rtcRef.current?.destroy();
    sigRef.current?.stop().catch(() => {});
    rtcRef.current = null;
    sigRef.current = null;
  }, []);

  // Stop local tracks ONLY when the component fully unmounts (not on every stream change,
  // because new streams share audio tracks with the old ones)
  useEffect(() => {
    return () => {
      localStreamRef.current?.getTracks().forEach(t => t.stop());
    };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // ─── Controls ─────────────────────────────────────────────────
  function toggleMic() {
    if (!localStream) return;
    const enabled = !micOn;
    localStream.getAudioTracks().forEach(t => { t.enabled = enabled; });
    setMicOn(enabled);
    sigRef.current?.updateMediaState(enabled, cameraOn, screenSharing);
  }

  async function toggleCamera() {
    if (!localStream) return;
    const enabling = !cameraOn;

    if (!enabling) {
      // 1. Tell every WebRTC sender to stop sending video — this is what actually
      //    releases the macOS green camera indicator (sender holds the track open)
      rtcRef.current?.replaceVideoTrack(null);
      // 2. Remove track from the local MediaStream and stop it (releases camera hardware)
      localStream.getVideoTracks().forEach(t => {
        localStream.removeTrack(t);
        t.stop();
      });
      setCameraOn(false);
      sigRef.current?.updateMediaState(micOn, false, screenSharing);
    } else {
      try {
        const camStream = await navigator.mediaDevices.getUserMedia({ video: true });
        const camTrack  = camStream.getVideoTracks()[0];
        // Add to local stream so the <video> element picks it up automatically
        localStream.addTrack(camTrack);
        // Push to all peer senders (stored reference handles null-sender peers too)
        rtcRef.current?.replaceVideoTrack(camTrack);
        setCameraOn(true);
        sigRef.current?.updateMediaState(micOn, true, screenSharing);
      } catch { /* permission denied */ }
    }
  }

  async function stopScreenShare() {
    // Release sender before stopping track so macOS camera light goes off immediately
    if (!cameraOnRef.current) rtcRef.current?.replaceVideoTrack(null);
    screenTrackRef.current?.stop();
    screenTrackRef.current = null;
    const curStream = localStreamRef.current;
    const audioTracks = curStream ? [...curStream.getAudioTracks()] : [];

    if (cameraOnRef.current) {
      try {
        const camStream = await navigator.mediaDevices.getUserMedia({ video: true });
        const camTrack  = camStream.getVideoTracks()[0];
        const withCam   = new MediaStream([...audioTracks, camTrack]);
        rtcRef.current?.setLocalStream(withCam);
        setLocalStream(withCam);
      } catch {
        const audioOnly = new MediaStream(audioTracks);
        rtcRef.current?.setLocalStream(audioOnly);
        setLocalStream(audioOnly);
      }
    } else {
      const audioOnly = new MediaStream(audioTracks);
      rtcRef.current?.setLocalStream(audioOnly);
      setLocalStream(audioOnly);
    }
    setScreenSharing(false);
    sigRef.current?.updateMediaState(micOnRef.current, cameraOnRef.current, false);
  }

  async function toggleScreenShare() {
    if (screenSharing) {
      await stopScreenShare();
    } else {
      try {
        // Hint browser to default to "Entire Screen" tab in the picker
        const screenStream = await navigator.mediaDevices.getDisplayMedia({
          video: { displaySurface: 'monitor' },
          preferCurrentTab: false,
        });
        const screenTrack = screenStream.getVideoTracks()[0];
        screenTrackRef.current = screenTrack;
        const withScreen = new MediaStream([...localStream.getAudioTracks(), screenTrack]);
        rtcRef.current?.setLocalStream(withScreen);
        setLocalStream(withScreen);
        setScreenSharing(true);
        sigRef.current?.updateMediaState(micOnRef.current, cameraOnRef.current, true);
        // Use refs so this callback always sees fresh state when browser stops share
        screenTrack.onended = () => stopScreenShare();
      } catch { /* user cancelled */ }
    }
  }

  // ─── Recording ────────────────────────────────────────────────
  // Captures the actual screen/tab (getDisplayMedia) rather than a custom composite,
  // so it always matches whatever is really on screen. The host picks "This Tab" (or
  // the whole screen) in the browser's native share prompt.
  async function handleStartRecording() {
    const rec = new MeetingRecorder();
    try {
      await rec.start(localStreamRef.current);
    } catch (e) {
      // User cancelled the share picker, or the browser blocked it — not a real error.
      if (e?.name !== 'NotAllowedError') {
        setRecordingMsg(e instanceof Error ? e.message : t('room.recordingUploadFailed'));
        window.setTimeout(() => setRecordingMsg(''), 4000);
      }
      return;
    }
    recorderRef.current = rec;
    setIsRecording(true);
    setRecordingMsg('');
  }

  // Stops the recorder, downloads the file to the host's device, and uploads it to
  // the company archive. Safe to call multiple times / when nothing is recording.
  async function handleStopRecordingAndSave() {
    const rec = recorderRef.current;
    recorderRef.current = null;
    setIsRecording(false);
    if (!rec) return;

    setRecordingBusy(true);
    setRecordingMsg(t('room.recordingProcessing'));
    try {
      const blob = await rec.stop();
      if (!blob || blob.size === 0) {
        setRecordingMsg(t('room.recordingEmpty'));
        return;
      }

      const safeTitle = (roomInfo?.meeting?.title || 'meeting').replace(/[\\/:*?"<>|]+/g, '_');
      const ext = blob.type && blob.type.includes('mp4') ? 'mp4' : 'webm';
      const fileName = `${safeTitle}-${Date.now()}.${ext}`;

      // 1. Auto-download to the host's own device
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = fileName;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);

      // 2. Upload + file into the company archive
      setRecordingMsg(t('room.recordingUploading'));
      await uploadMeetingRecording(token, blob, fileName);
      setRecordingMsg(t('room.recordingSaved'));
    } catch (e) {
      setRecordingMsg(e instanceof Error ? e.message : t('room.recordingUploadFailed'));
    } finally {
      setRecordingBusy(false);
      window.setTimeout(() => setRecordingMsg(''), 4000);
    }
  }

  function handleToggleRecording() {
    if (isRecording) handleStopRecordingAndSave();
    else handleStartRecording();
  }

  // ─── Chat handlers ────────────────────────────────────────────
  function handleToggleChat() {
    setChatOpen(prev => {
      if (!prev) setUnreadChat(0);
      return !prev;
    });
  }

  async function handleSendChat(type, content, fileName, fileSize) {
    await sigRef.current?.sendChatMessage(type, content, fileName, fileSize);
  }

  // ─── Vote handlers ────────────────────────────────────────────
  async function handleLaunchVote(title, options) {
    setCreateVoteOpen(false);
    await sigRef.current?.createVote(title, options);
  }

  async function handleCastVote(voteId, optionIndex) {
    setMyVotes(prev => new Map(prev).set(voteId, optionIndex));
    await sigRef.current?.castVote(voteId, optionIndex);
  }

  async function handleToggleResults(voteId, show) {
    await sigRef.current?.toggleVoteResults(voteId, show);
  }

  async function handleCloseVote(voteId) {
    await sigRef.current?.closeVote(voteId);
  }

  async function handleDismissVote(voteId) {
    await sigRef.current?.dismissVote(voteId);
  }

  async function handleSaveMeetingDetails(title, goal) {
    await sigRef.current?.updateMeetingDetails(title, goal);
    setRoomInfo(prev => prev ? { ...prev, meeting: { ...prev.meeting, title, goal } } : prev);
  }

  function handleCloseVotePopup(voteId) {
    // Hide popup locally — vote stays in decisions list
    setHiddenPopups(prev => new Set([...prev, voteId]));
    if (detailVoteId === voteId) setDetailVoteId(null);
  }

  async function handleLeave() {
    if (recorderRef.current) await handleStopRecordingAndSave();
    cleanup();
    localStream?.getTracks().forEach(t => t.stop());
    exitRoom();
  }

  async function handleEndMeeting() {
    if (recorderRef.current) await handleStopRecordingAndSave();
    sigRef.current?.endMeeting().catch(() => {});
    cleanup();
    localStream?.getTracks().forEach(t => t.stop());
    exitRoom();
  }

  // ─── Render states ────────────────────────────────────────────
  if (needsAuth) {
    return <MeetingRoomLogin onSuccess={() => { setAuthed(true); setNeedsAuth(false); }} />;
  }

  if (signInFromLobby) {
    return (
      <MeetingRoomLogin
        onSuccess={() => {
          setAuthed(true);
          setSignInFromLobby(false);
          setGuestName('');
          setGuestPhotoData(null);
        }}
        onCancel={() => setSignInFromLobby(false)}
      />
    );
  }

  if (loadError) {
    return (
      <div className="room">
        <div className="room__centered">
          <div className="room__centered-icon">
            <svg viewBox="0 0 24 24"><path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm1 15h-2v-2h2v2zm0-4h-2V7h2v6z"/></svg>
          </div>
          <h2>{t('room.notFound')}</h2>
          <p>{loadError}</p>
          <button className="room__btn" onClick={() => exitRoom()}>{t('room.goBack')}</button>
        </div>
      </div>
    );
  }

  if (!roomInfo) {
    return (
      <div className="room">
        <div className="room__centered">
          <div className="room__spinner" />
          <p>{t('room.loadingRoom')}</p>
        </div>
      </div>
    );
  }

  // ── Guest Waiting / Denied states ──
  if (guestWaitingState === 'waiting') {
    return (
      <div className="room">
        <div className="room__centered" style={{ padding: 32, background: '#fff', borderRadius: 12, textAlign: 'center', boxShadow: '0 4px 16px rgba(0,0,0,0.1)' }}>
          <div className="room__spinner" style={{ margin: '0 auto 16px' }} />
          <h2 style={{ fontSize: '1.4rem', marginBottom: 8 }}>{t('meetings.waitingForHost') || 'في انتظار موافقة المضيف...'}</h2>
          <p style={{ color: '#556976' }}>{t('meetings.waitingDesc') || 'لقد تم إرسال طلبك للانضمام، يرجى الانتظار.'}</p>
        </div>
      </div>
    );
  }

  if (guestWaitingState === 'denied') {
    return (
      <div className="room">
        <div className="room__centered" style={{ padding: 32, background: '#fff', borderRadius: 12, textAlign: 'center', boxShadow: '0 4px 16px rgba(0,0,0,0.1)' }}>
          <div className="room__centered-icon" style={{ background: '#fce4e4', margin: '0 auto 16px' }}>
            <svg viewBox="0 0 24 24" style={{ fill: '#e53935' }}><path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm1 15h-2v-2h2v2zm0-4h-2V7h2v6z"/></svg>
          </div>
          <h2 style={{ fontSize: '1.4rem', marginBottom: 8 }}>{t('meetings.entryDenied') || 'تم رفض طلب الدخول'}</h2>
          <p style={{ color: '#556976', marginBottom: 24 }}>{t('meetings.deniedDesc') || 'عذراً، لم يقم المضيف بالموافقة على طلب انضمامك للاجتماع.'}</p>
          <button className="btn btn-outline" onClick={() => window.location.reload()}>{t('meetings.tryAgain') || 'حاول مرة أخرى'}</button>
        </div>
      </div>
    );
  }

  // ── Lobby screen ──
  if (roomInfo && inLobby) {
    return (
      <MeetingLobby
        roomInfo={roomInfo}
        displayName={myName}
        photoData={myPhotoData}
        onJoin={handleLobbyJoin}
        onBack={() => exitRoom()}
        isGuest={!authed}
        guestName={guestName}
        onGuestNameChange={setGuestName}
        myPhotoData={guestPhotoData || myPhotoData}
        onSignIn={!authed ? () => setSignInFromLobby(true) : undefined}
      />
    );
  }

  if (permState === 'denied') {
    return (
      <div className="room">
        <div className="room__perm">
          <div className="room__centered-icon">
            <svg viewBox="0 0 24 24"><path d="M12 1C8.676 1 6 3.676 6 7v1H4v15h16V8h-2V7c0-3.324-2.676-6-6-6zm0 2c2.276 0 4 1.724 4 4v1H8V7c0-2.276 1.724-4 4-4zm0 9a2 2 0 1 1 0 4 2 2 0 0 1 0-4z"/></svg>
          </div>
          <h2>{t('room.permRequired')}</h2>
          <p>{t('room.permInstructions')}</p>
          <button className="room__btn" onClick={() => window.location.reload()}>{t('room.reloadPage')}</button>
          <button className="room__btn room__btn--ghost" onClick={() => exitRoom()}>{t('room.goBack')}</button>
        </div>
      </div>
    );
  }

  if (callState === 'ended') {
    return (
      <div className="room">
        <div className="room__centered">
          <div className="room__centered-icon" style={{ background: '#fce4e4' }}>
            <svg viewBox="0 0 24 24" style={{ fill: '#e53935' }}><path d="M6.6 10.8c1.4 2.8 3.8 5.1 6.6 6.6l2.2-2.2c.3-.3.7-.4 1-.2 1.1.4 2.3.6 3.6.6.6 0 1 .4 1 1V20c0 .6-.4 1-1 1-9.4 0-17-7.6-17-17 0-.6.4-1 1-1h3.5c.6 0 1 .4 1 1 0 1.3.2 2.5.6 3.6.1.3 0 .7-.2 1L6.6 10.8z"/></svg>
          </div>
          <h2>{t('room.meetingEnded')}</h2>
          <p>{t('room.hostEndedMeeting')}</p>
          <button className="room__btn" onClick={() => exitRoom()}>{t('room.goBack')}</button>
        </div>
      </div>
    );
  }

  const peersArr = Array.from(peers.values());

  return (
    <div className="room">
      {/* Header */}
      <header className="room__header">
        <div className="room__header-left">
          <div className="room__logo">
            <svg viewBox="0 0 24 24"><path d="M17 10.5V7c0-.55-.45-1-1-1H4c-.55 0-1 .45-1 1v10c0 .55.45 1 1 1h12c.55 0 1-.45 1-1v-3.5l4 4v-11l-4 4z"/></svg>
          </div>
          <div>
            <h1 className="room__title">
              {roomInfo.meeting?.title || 'Meeting'}
              {isHost && (
                <button
                  className="room__edit-title-btn"
                  onClick={() => setEditDetailsOpen(true)}
                  title={t('room.editDetails')}
                  aria-label={t('room.editDetails')}
                >
                  <svg viewBox="0 0 24 24"><path d="M3 17.25V21h3.75L17.81 9.94l-3.75-3.75L3 17.25zM20.71 7.04a1 1 0 0 0 0-1.41l-2.34-2.34a1 1 0 0 0-1.41 0l-1.83 1.83 3.75 3.75 1.83-1.83z"/></svg>
                </button>
              )}
            </h1>
            <p className="room__subtitle">{roomInfo.meeting?.goal || ''}</p>
          </div>
        </div>
        <div className="room__header-right">
          {isRecording && <span className="room__status room__status--recording">● {t('room.recordingLive')}</span>}
          {statusMsg && <span className="room__status room__status--waiting">{statusMsg}</span>}
          {!statusMsg && callState === 'active' && (
            <span className="room__status room__status--live">{t('room.live')}</span>
          )}
          {!statusMsg && callState === 'joining' && (
            <span className="room__status room__status--waiting">{t('room.connecting')}</span>
          )}
          <div style={{ marginInlineStart: '12px', display: 'flex', alignItems: 'center' }}>
            <LanguageSelector variant="icon" placement="bottom-end" />
          </div>
        </div>
      </header>

      {recordingMsg && <div className="room__recording-banner">{recordingMsg}</div>}

      {/* Video grid */}
      <main className="room__body">
        <VideoGrid
          localStream={localStream}
          localName={myName}
          localPhotoData={myPhotoData}
          localMicOn={micOn}
          localCameraOn={cameraOn}
          localScreenSharing={screenSharing}
          peers={peersArr}
        />
      </main>

      {/* Participants Panel: waiting join requests + current members */}
      {participantsOpen && (
        <div className="participants-panel">
          <div className="participants-panel__header">
            <span>{t('room.participants') || 'المشاركون'}</span>
            <button className="participants-panel__close" onClick={() => setParticipantsOpen(false)}>✕</button>
          </div>

          {/* Join Requests section — host only */}
          {isHost && waitingGuestsList.length > 0 && (
            <div className="participants-panel__section">
              <div className="participants-panel__section-title">
                <span className="waiting-guests-pulse" style={{ width: 8, height: 8 }} />
                {t('meetings.waitingRoom') || 'طلبات الانضمام'}
                <span className="waiting-guests-count">{waitingGuestsList.length}</span>
              </div>
              {waitingGuestsList.map(wg => (
                <div key={wg.guestToken} className="waiting-guest-card">
                  <div className="waiting-guest-avatar">{(wg.guestName || '?')[0].toUpperCase()}</div>
                  <div className="waiting-guest-info">
                    <div className="waiting-guest-name">{wg.guestName || t('meetings.guest') || 'زائر'}</div>
                    <div className="waiting-guest-label">{t('meetings.wantsToJoin') || 'يرغب بالانضمام'}</div>
                  </div>
                  <div className="waiting-guest-actions">
                    <button className="waiting-guest-btn waiting-guest-btn--admit" onClick={() => {
                      sigRef.current?.approveGuest(wg.guestToken);
                      setWaitingGuestsList(prev => prev.filter(g => g.guestToken !== wg.guestToken));
                    }}>
                      <svg viewBox="0 0 24 24" width="18" height="18"><path d="M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41z"/></svg>
                    </button>
                    <button className="waiting-guest-btn waiting-guest-btn--deny" onClick={() => {
                      sigRef.current?.denyGuest(wg.guestToken);
                      setWaitingGuestsList(prev => prev.filter(g => g.guestToken !== wg.guestToken));
                    }}>
                      <svg viewBox="0 0 24 24" width="18" height="18"><path d="M19 6.41L17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12z"/></svg>
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* Current participants list */}
          <div className="participants-panel__section">
            <div className="participants-panel__section-title">{t('room.inMeeting') || 'في الاجتماع'} ({1 + peersArr.length})</div>
            {/* Self */}
            <div className="participant-row">
              <div className="participant-row__avatar" style={{ background: 'linear-gradient(135deg, #02c3a6, #0891b2)' }}>
                {myPhotoData ? <img src={myPhotoData} alt="" style={{ width: '100%', height: '100%', borderRadius: '50%', objectFit: 'cover' }} /> : (myName[0] || '?').toUpperCase()}
              </div>
              <div className="participant-row__name">{myName} {isHost ? `(${t('room.host') || 'مضيف'})` : ''}</div>
            </div>
            {/* Peers */}
            {peersArr.map(p => (
              <div key={p.connectionId} className="participant-row">
                <div className="participant-row__avatar">
                  {p.photoData ? <img src={p.photoData} alt="" style={{ width: '100%', height: '100%', borderRadius: '50%', objectFit: 'cover' }} /> : (p.displayName?.[0] || '?').toUpperCase()}
                </div>
                <div className="participant-row__name">{p.displayName || t('meetings.participant') || 'مشارك'}</div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Controls */}
      <MeetingControls
        micOn={micOn}
        cameraOn={cameraOn}
        screenSharing={screenSharing}
        isHost={isHost}
        participantCount={1 + peersArr.length}
        waitingCount={waitingGuestsList.length}
        participantsOpen={participantsOpen}
        onToggleParticipants={() => setParticipantsOpen(v => !v)}
        chatOpen={chatOpen}
        unreadChat={unreadChat}
        decisionsOpen={decisionsOpen}
        decisionCount={votes.size}
        isRecording={isRecording}
        onToggleChat={handleToggleChat}
        onOpenVote={() => setCreateVoteOpen(true)}
        onToggleDecisions={() => setDecisionsOpen(v => !v)}
        onToggleMic={toggleMic}
        onToggleCamera={toggleCamera}
        onToggleScreen={toggleScreenShare}
        onToggleRecording={handleToggleRecording}
        onLeave={handleLeave}
        onEndMeeting={handleEndMeeting}
      />

      {/* Chat panel */}
      <MeetingChat
        open={chatOpen}
        onClose={() => setChatOpen(false)}
        messages={chatMsgs}
        myUserId={me?.id}
        onSend={handleSendChat}
        unread={unreadChat}
      />

      {/* Edit meeting title/description modal (host only) */}
      {editDetailsOpen && isHost && (
        <EditMeetingDetailsModal
          title={roomInfo.meeting?.title}
          goal={roomInfo.meeting?.goal}
          onSave={handleSaveMeetingDetails}
          onClose={() => setEditDetailsOpen(false)}
        />
      )}

      {/* Create vote modal (host only) */}
      {createVoteOpen && (
        <CreateVoteModal
          onLaunch={handleLaunchVote}
          onClose={() => setCreateVoteOpen(false)}
        />
      )}

      {/* Live vote popups — only for non-hidden votes */}
      {Array.from(votes.values())
        .filter(v => !hiddenPopups.has(v.voteId))
        .map(vote => (
          <VotePopup
            key={vote.voteId}
            vote={vote}
            myUserId={me?.id}
            myVote={myVotes.get(vote.voteId) ?? null}
            isCreator={vote.creatorId === me?.id}
            onCast={idx => handleCastVote(vote.voteId, idx)}
            onToggleResults={show => handleToggleResults(vote.voteId, show)}
            onCloseVote={() => handleCloseVote(vote.voteId)}
            onDismiss={() => handleDismissVote(vote.voteId)}
            onClose={() => handleCloseVotePopup(vote.voteId)}
          />
        ))
      }

      {/* Decisions list panel */}
      {decisionsOpen && (
        <DecisionsList
          votes={Array.from(votes.values())}
          myVotes={myVotes}
          myUserId={me?.id}
          detailVoteId={detailVoteId}
          onSelectVote={voteId => {
            setDetailVoteId(voteId);
            // Re-show popup if it was hidden
            setHiddenPopups(prev => { const n = new Set(prev); n.delete(voteId); return n; });
          }}
          onClose={() => { setDecisionsOpen(false); setDetailVoteId(null); }}
          onCast={(voteId, idx) => handleCastVote(voteId, idx)}
          onToggleResults={(voteId, show) => handleToggleResults(voteId, show)}
          onCloseVote={voteId => handleCloseVote(voteId)}
          onDismiss={voteId => handleDismissVote(voteId)}
          isHost={isHost}
        />
      )}

      {/* Blocks interaction briefly while the recording is being saved on exit */}
      {recordingBusy && (
        <div className="room__saving-overlay">
          <div className="room__spinner" />
          <p>{recordingMsg || t('room.recordingProcessing')}</p>
        </div>
      )}
    </div>
  );
}
